import { useEffect, useRef } from 'react';

/**
 * Custom hook that triggers a callback when clicking outside of the referenced element
 * @param {Function} callback - Function to call when clicking outside
 * @param {Array} excludeRefs - Optional array of refs to exclude from outside click detection
 * @returns {Object} ref - Ref to attach to the element you want to detect outside clicks for
 */
export const useClickOutside = (callback, excludeRefs = []) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the ref exists and if the clicked element is outside the ref
      if (ref.current && !ref.current.contains(event.target)) {
        // Check if the click is inside any of the excluded refs
        const isClickInsideExcluded = excludeRefs.some(
          (excludeRef) =>
            excludeRef.current && excludeRef.current.contains(event.target)
        );

        if (!isClickInsideExcluded) {
          callback(event);
        }
      }
    };

    // Add event listener for mousedown and touchstart events
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [callback, excludeRefs]);

  return ref;
};
